// Towing plan configuration for the 5D Autostacker demo
// All positions are in scene-space feet. Sweep path points are [x, y, z] control points
// for a cubic Bezier rendered on the hangar floor (y ≈ 0.3ft to float just above floor).
//
// Building rotation: 269.15° — doors face west (-X direction in scene).
// Back wall faces east (+X). Aircraft nose-in = rotation 90° (nose pointing east).
//
// Tow sequence triggered after Global 8000 is assigned to hangar via AutoFit.

export type TowStepType = 'hold' | 'reposition' | 'park'

export interface TowStep {
  id: string
  stepNumber: number
  type: TowStepType
  allocationId: string
  aircraftLabel: string    // Display name shown in sidebar
  tailNumber: string
  description: string      // Short description shown in step card
  narrative: string        // Longer script line for the sales presenter
  fromPosition: [number, number, number]
  fromRotation: number
  toPosition: [number, number, number]
  toRotation: number
  // Bezier control points for the sweep path line on the floor
  // [p0=start, cp1=control1, cp2=control2, p3=end], y=0.3 to float above floor
  sweepPath: [[number, number, number], [number, number, number], [number, number, number], [number, number, number]]
  pathColor: string
  durationMinutes: number
  crewRequired: number
}

export interface TowingPlanSummary {
  totalMoves: number
  totalMinutes: number
  crewRequired: number
  revenuePerNight: number
  totalNights: number
  totalRevenue: number
  minClearanceFt: number
  riskLevel: 'low' | 'medium' | 'high'
  capacityAfter: string   // e.g. "94%"
}

// Scene position constants (matching demoConfig.ts seed positions)
// Building center ≈ [702, 0, -664]. Doors face west (≈ scene X=602).

export const TOWING_PLAN: TowStep[] = [
  {
    id: 'step-0-hold',
    stepNumber: 0,
    type: 'hold',
    allocationId: 'a110c380-0009-4000-a000-000000000001', // Global 8000
    aircraftLabel: 'Global 8000 (000022)',
    tailNumber: '000022',
    description: 'Smart Hold — stage on ramp while CJ4 repositions',
    narrative:
      'AV8 sees CJ4 departs at 2 PM tomorrow. Instead of parking the Global 8000 twice, it stages a 15-minute Smart Hold — saving your crew an entire round-trip tow.',
    fromPosition: [490, 0, -666],
    fromRotation: 270,
    toPosition: [490, 0, -666], // stays put during hold
    toRotation: 270,
    sweepPath: [[490, 0.3, -666], [486, 0.3, -660], [486, 0.3, -672], [490, 0.3, -666]],
    pathColor: '#F59E0B', // amber — waiting
    durationMinutes: 15,
    crewRequired: 1,
  },
  {
    id: 'step-1-cj4',
    stepNumber: 1,
    type: 'reposition',
    allocationId: 'a110c380-0007-4000-a000-000000000001', // Citation CJ4
    aircraftLabel: 'Citation CJ4 (N98765B)',
    tailNumber: 'N98765B',
    description: 'Reposition CJ4 to door-right for easy 2 PM departure',
    narrative:
      'We tow the CJ4 to a door-right position so it can taxi out at 2 PM without disturbing anything. 8 minutes, done.',
    fromPosition: [662, 0, -650],
    fromRotation: 90,
    toPosition: [616, 0, -608],
    toRotation: 270,
    sweepPath: [
      [662, 0.3, -650],
      [644, 0.3, -638],
      [630, 0.3, -622],
      [616, 0.3, -608],
    ],
    pathColor: '#00D4FF',
    durationMinutes: 8,
    crewRequired: 2,
  },
  {
    id: 'step-2-challenger',
    stepNumber: 2,
    type: 'reposition',
    allocationId: 'a110c380-0004-4000-a000-000000000001', // Challenger 350 (departs tomorrow 10:00)
    aircraftLabel: 'Challenger 350 (N12354YT)',
    tailNumber: 'N12354YT',
    description: 'Pre-stage Challenger at door-left for 10 AM departure',
    narrative:
      "The Challenger departs at 10 AM tomorrow. AV8 pre-stages it at the door-left slot — clears the front-left bay AND saves a morning tow. Two birds, one move.",
    fromPosition: [665, 0, -724],
    fromRotation: 90,
    toPosition: [614, 0, -730],
    toRotation: 270,
    sweepPath: [
      [665, 0.3, -724],
      [648, 0.3, -726],
      [630, 0.3, -728],
      [614, 0.3, -730],
    ],
    pathColor: '#00D4FF',
    durationMinutes: 7,
    crewRequired: 2,
  },
  {
    id: 'step-3-global8000',
    stepNumber: 3,
    type: 'park',
    allocationId: 'a110c380-0009-4000-a000-000000000001', // Global 8000
    aircraftLabel: 'Global 8000 (000022)',
    tailNumber: '000022',
    description: 'Tow Global 8000 through door into cleared front-left slot',
    narrative:
      "The Smart Hold paid off. We tow the Global 8000 straight in — a clean 12-minute maneuver, 4-foot clearance on each wingtip. $9,600 in overnight revenue, secured.",
    fromPosition: [490, 0, -666],
    fromRotation: 270,
    toPosition: [648, 0, -724],
    toRotation: 90,
    sweepPath: [
      [490, 0.3, -666],
      [540, 0.3, -678],
      [586, 0.3, -698],
      [648, 0.3, -724],
    ],
    pathColor: '#10B981',
    durationMinutes: 12,
    crewRequired: 2,
  },
]

export const TOWING_SUMMARY: TowingPlanSummary = {
  totalMoves: 3,       // Step 0 is a hold (automated), steps 1-3 are tow moves
  totalMinutes: 41,
  crewRequired: 2,
  revenuePerNight: 3200,
  totalNights: 3,
  totalRevenue: 9600,
  minClearanceFt: 4,
  riskLevel: 'low',
  capacityAfter: '94%',
}

// Progress messages shown during the 7-second "computing" animation
export const PLANNING_STEPS = [
  { label: 'Connecting to Cloud...',       icon: 'cloud',     durationMs: 1400 },
  { label: 'Building Time Graph...',       icon: 'timeline',  durationMs: 1400 },
  { label: 'Analyzing Resources...',       icon: 'users',     durationMs: 1400 },
  { label: 'Spatial Calculations...',      icon: 'cube',      durationMs: 1400 },
  { label: 'Verifying Tow Constraints...', icon: 'route',     durationMs: 1400 },
] as const
